/**
 * <Crypto> — 암호화폐 (BTC + ETH)
 * 위험자산 선행지표. 미장 시작 전 가장 먼저 체크.
 * 데이터: GET /api/ticker/market/crypto → { btc: {...}, eth: {...} }
 */
"use client";

import { useEffect, useRef, useState } from "react";
import type { WidgetMeta, WidgetProps } from "../types";

interface CryptoQuote {
  symbol: string;
  label: string;
  name?: string;    // 사용자 결정 #9: name + ticker 분리
  ticker?: string;
  price: number | null;
  changePercent: number | null;
  change: number | null;
}

interface CryptoData {
  btc: CryptoQuote | null;
  eth: CryptoQuote | null;
  sol: CryptoQuote | null;
  doge: CryptoQuote | null;
  xrp: CryptoQuote | null;
  bnb: CryptoQuote | null;
}

const REFRESH_MS = 60 * 1000;

// 사용자 결정 #5: 코인별 원형 아이콘 — CoinGecko 공식 CDN 이미지
//   안정적 + 무료 + 모든 메이저 코인 커버
//   fallback: 빈 회색 원
const COIN_ICON: Record<string, string> = {
  "BTC-USD":  "https://assets.coingecko.com/coins/images/1/small/bitcoin.png",
  "ETH-USD":  "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
  "SOL-USD":  "https://assets.coingecko.com/coins/images/4128/small/solana.png",
  "DOGE-USD": "https://assets.coingecko.com/coins/images/5/small/dogecoin.png",
  "XRP-USD":  "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
  "BNB-USD":  "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png",
};

function fmtPct(v: number | null) {
  if (v == null || !Number.isFinite(v)) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${(v * 100).toFixed(2)}%`;
}
function fmtPrice(v: number | null, decimals = 2) {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function Row({ q }: { q: CryptoQuote | null }) {
  const pct = q?.changePercent ?? null;
  const up = pct != null && pct > 0;
  const down = pct != null && pct < 0;
  const iconUrl = q?.symbol ? COIN_ICON[q.symbol] : null;
  // 사용자 결정 #9: 이름 (메인) + ticker (secondary). label fallback (legacy)
  const name = q?.name || q?.label?.split(" (")[0] || "—";
  const ticker = q?.ticker || q?.symbol?.replace("-USD", "") || "";
  return (
    <div className="dw-fx-row dw-crypto-row">
      <div className="dw-fx-label dw-crypto-label">
        {iconUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={iconUrl} alt="" className="dw-crypto-icon" loading="lazy" />
        )}
        <span className="dw-crypto-name">{name}</span>
        {ticker && <span className="dw-crypto-ticker">{ticker}</span>}
      </div>
      <div className="dw-fx-price">{fmtPrice(q?.price ?? null, (q?.price ?? 0) > 1000 ? 0 : 2)}</div>
      <div className={`dw-fx-pct ${up ? "up" : down ? "down" : ""}`}>{fmtPct(pct)}</div>
    </div>
  );
}

export function Crypto(_props: WidgetProps) {
  const [data, setData] = useState<CryptoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/market/crypto", { credentials: "include" });
        if (!res.ok) {
          if (mountedRef.current) { setError("로드 실패"); setLoading(false); }
          return;
        }
        const d = await res.json();
        if (mountedRef.current) { setData(d); setError(null); setLoading(false); }
      } catch {
        if (mountedRef.current) { setError("네트워크 오류"); setLoading(false); }
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { mountedRef.current = false; clearInterval(t); };
  }, []);

  return (
    <div className="dw-body dw-fx">
      <div className="dw-head"><span className="dw-title">암호화폐</span></div>
      {error ? (
        <div className="dw-fx-error">{error}</div>
      ) : loading && !data ? (
        <div className="dw-fx-empty">로딩 중…</div>
      ) : (
        // 사용자 결정 #12 fix: 내부 스크롤
        <div className="dw-fx-list dw-crypto-list dw-crypto-list--scroll">
          <Row q={data?.btc ?? null} />
          <Row q={data?.eth ?? null} />
          <Row q={data?.sol ?? null} />
          <Row q={data?.doge ?? null} />
          <Row q={data?.xrp ?? null} />
          <Row q={data?.bnb ?? null} />
        </div>
      )}
    </div>
  );
}

export const cryptoMeta: WidgetMeta = {
  type: "crypto",
  name: "암호화폐",
  description: "BTC · ETH · SOL · DOGE · XRP · BNB · 위험자산 선행지표",
  icon: "₿",
  category: "market",
  defaultSize: { w: 4, h: 4 },
  availableSizes: [
    { w: 3, h: 4 },
    { w: 4, h: 4 },
    { w: 4, h: 5 },
  ],
  minW: 3,
  maxW: 6,
  minH: 4,
  maxH: 6,
  component: Crypto,
};
